import React from 'react';
import { DeleteOutlined, InboxOutlined, SearchOutlined } from '@ant-design/icons';
import { RuleMode, ScopeLevel } from '../../types/returnInspection';

/**
 * 规则页的统一视觉约定（链路面板、表格、命中阶梯、编辑抽屉共用）：
 * - "层级"用固定颜色区分（通用=蓝、项目级=橘、库存单元级=绿）
 * - "处理方式"用 图标 + 直白文字，不再用彩色 Tag——红色只留给"销毁"和"无规则"这类需要提醒的状态，
 *   避免用户分不清"颜色代表层级还是代表处理方式"
 */

export const LEVEL_COLOR: Record<ScopeLevel, string> = {
  customer: '#1677ff',
  project: '#d46b08',
  inventory_unit: '#389e0d',
};

/** 层级的短名称，用在链路面板/阶梯/面包屑里 */
export const LEVEL_NAME: Record<ScopeLevel, string> = {
  customer: '通用规则',
  project: '项目级规则',
  inventory_unit: '库存单元级规则',
};

export const WARNING_COLOR = '#cf1322';

/** 处理方式的直白说法：关心的是"这类退货最后会怎么处理"，而不是模式的内部名称 */
export const MODE_OUTCOME: Record<RuleMode, string> = {
  always_destroy: '直接销毁',
  always_aiu: '直接作为异常库存（AIU）入库',
  conditional: '逐件初检（外观 → 内件）',
};

const MODE_ICON: Record<RuleMode, React.ReactNode> = {
  always_destroy: <DeleteOutlined />,
  always_aiu: <InboxOutlined />,
  conditional: <SearchOutlined />,
};

/** 处理方式：图标 + 直白文字，统一用默认文字色（销毁也不标红） */
export const ModeBadge: React.FC<{ mode: RuleMode | null | undefined; bold?: boolean }> = ({ mode, bold }) => {
  if (!mode) return null;
  return (
    <span style={{ fontWeight: bold ? 600 : undefined }}>
      <span style={{ marginRight: 6 }}>{MODE_ICON[mode]}</span>
      {MODE_OUTCOME[mode]}
    </span>
  );
};

/** 层级徽标：固定颜色的小色块 + 层级名；outlined 用于"路径里非当前层级"的弱化展示 */
export const LevelBadge: React.FC<{
  level: ScopeLevel;
  text?: string;
  outlined?: boolean;
  muted?: boolean;
}> = ({ level, text, outlined, muted }) => {
  const color = muted ? '#bfbfbf' : LEVEL_COLOR[level];
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '0 8px',
        lineHeight: '20px',
        borderRadius: 4,
        fontSize: 12,
        whiteSpace: 'nowrap',
        border: `1px solid ${color}`,
        color: outlined ? color : '#fff',
        background: outlined ? 'transparent' : color,
      }}
    >
      {text || LEVEL_NAME[level]}
    </span>
  );
};
